import * as React from 'react';
import {useEffect, useState} from "react";
import {Link, useHistory} from 'react-router-dom';
import {addUser, addEsignUser, uploadEsignSignature} from "../../../api";

const SIGNATURE_WIDTH = 240;
const SIGNATURE_HEIGHT = 50;

// Draws the chosen image onto a 240x50 canvas (aspect-preserving, centered) and returns a PNG blob.
const resizeToSignaturePng = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("could not read image file"));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error("could not load image"));
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = SIGNATURE_WIDTH;
                canvas.height = SIGNATURE_HEIGHT;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("could not get canvas context"));
                    return;
                }
                ctx.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
                const scale = Math.min(SIGNATURE_WIDTH / img.width, SIGNATURE_HEIGHT / img.height);
                const drawW = img.width * scale;
                const drawH = img.height * scale;
                ctx.drawImage(img, (SIGNATURE_WIDTH - drawW) / 2, (SIGNATURE_HEIGHT - drawH) / 2, drawW, drawH);
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error("could not create png"));
                    }
                }, "image/png");
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    });
};

const AddUser: React.FunctionComponent = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [role, setRole] = useState("");
    const [signatureFile, setSignatureFile] = useState<File | undefined>(undefined);
    const [signaturePreview, setSignaturePreview] = useState<string | undefined>(undefined);
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [doRedirectToSearch, setDoRedirectToSearch] = useState(false);
    const history = useHistory();

    useEffect(() => {
        if (doRedirectToSearch) {
            history.push({pathname: "/m/users"});
        }
    }, [doRedirectToSearch, history]);

    const onSignatureSelected = (file?: File) => {
        setSignatureFile(file);
        if (file) {
            setSignaturePreview(URL.createObjectURL(file));
        } else {
            setSignaturePreview(undefined);
        }
    };

    const doAdd = async () => {
        setMessage("");
        if (!signatureFile) {
            setMessage("Please upload a signature image.");
            return;
        }
        setSubmitting(true);
        try {
            // 1) Resize the signature to a 240x50 png and upload it to the esign file store.
            const png = await resizeToSignaturePng(signatureFile);
            const uploaded = await uploadEsignSignature(png, "signature.png");
            const signatureUrl = "/esign-files/" + uploaded.uuid + "/" + uploaded.name;

            // 2) Create the user in esign. This must succeed before we persist to our database.
            await addEsignUser({
                email: email,
                role: role.toLowerCase(),
                signature: signatureUrl,
                activated: true
            });

            // 3) esign succeeded, so persist the user to the promises12 database.
            await addUser({
                email: email,
                password: password,
                firstName: firstName,
                lastName: lastName,
                role: role
            });

            setDoRedirectToSearch(true);
        } catch (e) {
            console.log(e);
            setMessage("Failed to add user. The esign service must succeed before the user is created.");
        } finally {
            setSubmitting(false);
        }
    };

    return (<>
        <div className={"row"}>
            <div className={"col-lg-12 mb-3 "}>
                <h2>Add User</h2>
            </div>
            <form onSubmit={(e) => {
                e.preventDefault();
                doAdd();
            }}>
                <div className={"col-lg-12 mb-3 "}>

                    <div className="form-row align-items-center">

                        <div className="col-sm-4 my-1">
                            <label htmlFor="inputEmail">Email</label>
                            <input type="email" className="form-control" id="inputEmail" required
                                   value={email} onChange={(e) => setEmail(e.target.value)}/>
                        </div>
                        <div className="col-sm-4 my-1">
                            <label htmlFor="inputPassword">Password</label>
                            <input type="password" className="form-control" id="inputPassword" required
                                   value={password} onChange={(e) => setPassword(e.target.value)}/>
                        </div>
                        <div className="col-sm-4 my-1">
                            <label htmlFor="inputRole">Role</label>
                            <select id="inputRole" className="form-control" required
                                    value={role} onChange={(e) => setRole(e.target.value)}>
                                <option value="">Choose...</option>
                                <option value="ADMIN">Admin</option>
                                <option value="THERAPIST">Therapist</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-row align-items-center">
                        <div className="col-sm-6 my-1">
                            <label htmlFor="inputFirstName">First Name</label>
                            <input type="text" className="form-control" id="inputFirstName" required
                                   value={firstName} onChange={(e) => setFirstName(e.target.value)}/>
                        </div>
                        <div className="col-sm-6 my-1">
                            <label htmlFor="inputLastName">Last Name</label>
                            <input type="text" className="form-control" id="inputLastName" required
                                   value={lastName} onChange={(e) => setLastName(e.target.value)}/>
                        </div>
                    </div>

                    <div className="form-row align-items-center">
                        <div className="col-sm-6 my-1">
                            <label htmlFor="inputSignature">Signature Image (resized to 240x50 png)</label>
                            <input type="file" accept="image/*" className="form-control-file" id="inputSignature"
                                   onChange={(e) => onSignatureSelected(e.target.files?.[0])}/>
                        </div>
                        <div className="col-sm-6 my-1">
                            {signaturePreview &&
                                <img alt={"signature preview"} src={signaturePreview}
                                     style={{width: SIGNATURE_WIDTH, height: SIGNATURE_HEIGHT, border: "1px solid #ced4da", objectFit: "contain"}}/>
                            }
                        </div>
                    </div>
                </div>

                {message &&
                    <div className={"col-lg-12 mb-3 "}>
                        <div className="alert alert-danger" role="alert">{message}</div>
                    </div>
                }

                <div className={"col-lg-12 mb-3 "}>
                    <Link to={"/m/users"}>
                        <button type={"button"} className={"btn btn-danger mr-3"}>
                            Cancel
                        </button>
                    </Link>
                    <button type={"submit"} className={"btn btn-primary"} disabled={submitting}>
                        {submitting ? "Adding..." : "Add User"}
                    </button>
                </div>
            </form>
        </div>
    </>);
};

export default AddUser;
